// Shared chart scaffolding. Every chart is a <figure> with:
//   - an accessible name (`label`), on the figure
//   - the visual, hidden from assistive tech (the figure says it in words)
//   - a visible one-sentence takeaway (`summary`): a chart has a job, and
//     this line states it, so the reader never has to decode the marks
//   - a visually hidden data table with every value, the full text
//     alternative for screen readers
// Colour comes from JAL Core tokens only (--viz-*, --color-*), never hex.
import type { ReactNode } from "react";
import { formatNumber } from "../format";

/**
 * Categorical order for series and parts. Red (--viz-5) and green (--viz-4)
 * come last so a category never reads as a status by accident.
 */
export const SERIES_COLORS = ["var(--viz-1)", "var(--viz-2)", "var(--viz-3)", "var(--viz-6)", "var(--viz-4)"] as const;

/** The single-series hue and its quiet context tint for highlight charts. */
export const SERIES_MAIN = "var(--viz-1)";
export const SERIES_CONTEXT = "color-mix(in oklab, var(--viz-1) 32%, var(--color-surface))";

export type ChartHeight = "sm" | "md" | "lg";

export type ValueFormat = (n: number) => string;

export const defaultFormat: ValueFormat = formatNumber;

export interface ChartBaseProps {
  /** Accessible name of the figure, e.g. "Pendaftar per program studi". */
  label: string;
  /** One visible sentence: what the reader should take away. */
  summary: ReactNode;
  /** Header of the value column in the data table, e.g. "Pendaftar". */
  valueLabel: string;
  /** Header of the category column in the data table, e.g. "Program studi". */
  categoryLabel: string;
  /** Value formatter, default id-ID integers. */
  format?: ValueFormat;
}

export interface SrTableProps {
  caption: string;
  head: string[];
  rows: (string | number)[][];
}

/**
 * The text alternative: a data table, visually hidden, read in full. It is a
 * 1px block in flow (not absolutely placed), so it never sits over a sibling;
 * it scrolls inside itself, so its table never spills past its box, and it
 * stays out of the tab order.
 */
export function SrTable({ caption, head, rows }: SrTableProps) {
  return (
    <div className="con-sr-block" tabIndex={-1}>
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) =>
                j === 0 ? (
                  <th key={j} scope="row">
                    {c}
                  </th>
                ) : (
                  <td key={j}>{c}</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ChartFigureProps {
  label: string;
  summary: ReactNode;
  kind: string;
  /** Plot box height for SVG charts; omitted for HTML bar charts that size to content. */
  height?: ChartHeight;
  table: SrTableProps;
  /** Legend or other visible text that belongs to the chart. */
  legend?: ReactNode;
  children: ReactNode;
}

export function ChartFigure({ label, summary, kind, height, table, legend, children }: ChartFigureProps) {
  return (
    <figure className="con-chart" data-chart={kind} data-height={height} aria-label={label}>
      {legend}
      <div className="con-chart-plot" aria-hidden="true">
        {children}
      </div>
      <figcaption className="con-chart-caption">
        <p className="con-chart-summary">{summary}</p>
        <SrTable {...table} />
      </figcaption>
    </figure>
  );
}

/** Hover readout for the SVG charts: surface, one hairline, no shadow. */
export function ChartTooltip({
  active,
  payload,
  label,
  format = defaultFormat,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ name?: unknown; value?: unknown; color?: string; payload?: unknown }>;
  label?: unknown;
  format?: ValueFormat;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="con-tooltip">
      {label !== undefined && label !== "" ? <p className="con-tooltip-label">{String(label)}</p> : null}
      {payload.map((p, i) => (
        <p className="con-tooltip-row" key={i}>
          <span>{String(p.name ?? "")}</span>
          <span className="con-tooltip-value">{typeof p.value === "number" ? format(p.value) : String(p.value ?? "")}</span>
        </p>
      ))}
    </div>
  );
}

/** Y axis width that fits the longest formatted tick, never a clipped number. */
export function yAxisWidth(values: number[], format: ValueFormat): number {
  const longest = values.reduce((m, v) => Math.max(m, format(v).length), 1);
  return Math.max(24, longest * 8 + 8);
}
